import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';
import { auth } from '../../firebase';
import { UserProfile, Gender } from '../../types';
import './RegistrationView.css';

interface RegistrationViewProps {
  onClose: () => void;
  setIsLoggedIn: (value: boolean) => void;
}

const RegistrationView: React.FC<RegistrationViewProps> = ({ onClose, setIsLoggedIn }) => {
  const [profile, setProfile] = useState<UserProfile>({
    id: '',
    firstName: '',
    lastName: '',
    gender: Gender.Male,
    dateOfBirth: new Date(),
    grade: '',
    school: '',
    phoneNumber: '',
    email: '',
    heightFeet: 0,
    heightInches: 0,
    weight: 0
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [schools, setSchools] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    try {
      const { db } = await import('../../firebase');
      const schoolsRef = collection(db, 'schools');
      const snapshot = await getDocs(schoolsRef);
      
      // School names are stored as document IDs, not in document data
      const schoolList = snapshot.docs.map(doc => doc.id);
      setSchools(schoolList.sort());
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading schools:', error);
      setSchools(['Sample High School', 'Another High School']); // Fallback
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!profile.firstName || !profile.lastName || !profile.email || !password) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      return;
    }

    setIsRegistering(true);
    setErrorMessage(null);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, profile.email, password);
      const user = userCredential.user;

      // Save profile to Firestore with the same structure as iOS
      const { db } = await import('../../firebase');
      
      // Save user profile with all fields matching iOS structure
      await setDoc(doc(db, 'users', user.uid), {
        id: user.uid,
        firstName: profile.firstName,
        lastName: profile.lastName,
        gender: profile.gender,
        dateOfBirth: profile.dateOfBirth,
        grade: profile.grade,
        school: profile.school,
        phoneNumber: profile.phoneNumber,
        email: profile.email,
        heightFeet: profile.heightFeet || 0,
        heightInches: profile.heightInches || 0,
        heightInCm: ((profile.heightFeet || 0) * 30.48) + ((profile.heightInches || 0) * 2.54),
        weight: profile.weight || 0,
        weightInKg: (profile.weight || 0) * 0.453592,
        createdAt: new Date(),
        // Additional Firebase Auth metadata
        uid: user.uid,
        isEmailVerified: user.emailVerified,
        lastSignInDate: user.metadata.lastSignInTime || new Date(),
        creationDate: user.metadata.creationTime || new Date(),
        // Initialize additional fields for compatibility
        totalWorkouts: 0,
        currentStreak: 0
      });

      // Create initial max lift entries with zero values (matching iOS)
      const maxLiftId = `${user.uid}_${Date.now()}`;
      await setDoc(doc(db, 'maxLifts', maxLiftId), {
        id: maxLiftId,
        userId: user.uid,
        benchPress: 0,
        deadlift: 0,
        squat: 0,
        clean: 0,
        dateCreated: new Date()
      });

      // Send email verification (matching iOS)
      await sendEmailVerification(user);
      
      console.log('User registered successfully:', user.uid);
      setIsLoggedIn(true);
    } catch (error: any) {
      console.error('Error registering user:', error);
      setErrorMessage(`Failed to register: ${error.message}`);
    }

    setIsRegistering(false);
  };

  const updateProfile = (field: keyof UserProfile, value: any) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="registration-view">
        <div className="registration-form">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-view">
      <div className="registration-form">
        <div className="form-header">
          <h1>Create Account</h1>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleRegister} className="form-content">
          <div className="form-group">
            <input
              type="text"
              placeholder="First Name *"
              value={profile.firstName}
              onChange={(e) => updateProfile('firstName', e.target.value)}
              className="text-field"
              required
            />
          </div>

          <div className="form-group">
            <input
              type="text"
              placeholder="Last Name *"
              value={profile.lastName}
              onChange={(e) => updateProfile('lastName', e.target.value)}
              className="text-field"
              required
            />
          </div>

          <div className="form-group">
            <label>Gender</label>
            <div className="gender-picker">
              {Object.values(Gender).map((gender) => (
                <label key={gender} className="radio-option">
                  <input
                    type="radio"
                    value={gender}
                    checked={profile.gender === gender}
                    onChange={(e) => updateProfile('gender', e.target.value)}
                  />
                  <span>{gender}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Date of Birth</label>
            <input
              type="date"
              value={profile.dateOfBirth.toISOString().split('T')[0]}
              onChange={(e) => updateProfile('dateOfBirth', new Date(e.target.value))}
              className="text-field"
            />
          </div>

          <div className="form-group">
            <label>Grade</label>
            <select
              value={profile.grade}
              onChange={(e) => updateProfile('grade', e.target.value)}
              className="text-field"
            >
              <option value="">Select Grade</option>
              {Array.from({ length: 6 }, (_, i) => i + 7).map(grade => (
                <option key={grade} value={grade.toString()}>{grade}</option>
              ))}
              <option value="Coach">Coach</option>
            </select>
          </div>

          <div className="form-group">
            <label>Height</label>
            <div className="height-inputs">
              <input
                type="number"
                placeholder="Feet"
                value={profile.heightFeet || ''}
                onChange={(e) => updateProfile('heightFeet', Math.max(0, Math.min(8, parseInt(e.target.value) || 0)))}
                className="text-field height-field"
                min="0"
                max="8"
              />
              <span>ft</span>
              <input
                type="number"
                placeholder="Inches"
                value={profile.heightInches || ''}
                onChange={(e) => updateProfile('heightInches', Math.max(0, Math.min(11, parseInt(e.target.value) || 0)))}
                className="text-field height-field"
                min="0"
                max="11"
              />
              <span>in</span>
            </div>
          </div>

          <div className="form-group">
            <input
              type="number"
              placeholder="Weight (lbs)"
              value={profile.weight || ''}
              onChange={(e) => updateProfile('weight', parseFloat(e.target.value) || 0)}
              className="text-field"
              min="0"
              step="0.1"
            />
          </div>

          <div className="form-group">
            <label>School</label>
            <select
              value={profile.school}
              onChange={(e) => updateProfile('school', e.target.value)}
              className="text-field"
            >
              <option value="">Select School</option>
              {schools.map(school => (
                <option key={school} value={school}>{school}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <input
              type="tel"
              placeholder="Phone Number (Optional)"
              value={profile.phoneNumber}
              onChange={(e) => updateProfile('phoneNumber', e.target.value)}
              className="text-field"
            />
          </div>

          <div className="form-group">
            <input
              type="email"
              placeholder="Email *"
              value={profile.email}
              onChange={(e) => updateProfile('email', e.target.value)}
              className="text-field"
              required
            />
          </div>

          <div className="form-group">
            <input
              type="password"
              placeholder="Password *"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="text-field"
              required
              minLength={6}
            />
          </div>

          <div className="form-group">
            <input
              type="password"
              placeholder="Confirm Password *"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="text-field"
              required
            />
          </div>

          {errorMessage && (
            <div className="error-message">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="register-button"
            disabled={isRegistering}
          >
            {isRegistering ? (
              <div className="loading-spinner small"></div>
            ) : (
              'Create Account'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegistrationView;
